import chroma from "chroma-js";
import classNames from "classnames";
import type { JSX } from "react";
import { useState } from "react";
import Select from "react-select";
import type { SingleValue } from "react-select";
import Creatable from "react-select/creatable";
import styles from "./Search.module.css";

interface SearchOption {
  value: string;
  label: string;
}

interface SearchProps {
  className?: string;
  creatable?: boolean;
  filterOption?: (option: any, inputValue: string) => boolean;
  formatOptionLabel?: (option: any, input: Record<string, any>) => JSX.Element;
  hideUnlessSearch?: boolean;
  inError?: boolean;
  noOptionsMessage?: string;
  onBlur: () => void;
  onChange: (value?: string) => void;
  options: SearchOption[];
  placeholder?: string;
  required?: boolean;
  requiredPlaceholder?: string;
  value?: string;
}

function Search({
  className = "",
  creatable,
  filterOption,
  formatOptionLabel,
  hideUnlessSearch,
  inError,
  noOptionsMessage,
  onBlur,
  onChange,
  options,
  placeholder,
  required,
  requiredPlaceholder,
  value,
}: SearchProps) {
  let selectedValue: SearchOption | null =
    options?.find((option) => option.value == value) ?? null;
  if (!selectedValue && value) selectedValue = { value, label: value };
  const fieldPlaceholder = required ? requiredPlaceholder : placeholder;
  const [menuIsOpen, setMenuIsOpen] = useState(
    hideUnlessSearch ? false : undefined,
  );

  const SelectComp = creatable ? Creatable : Select;

  return (
    <>
      <SelectComp
        components={{
          DropdownIndicator: () => (
            <span className="inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                x-description="Heroicon name: solid/selector"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill={
                  inError
                    ? chroma("red").set("hsl.s", 0.63).set("hsl.l", 0.56).hex()
                    : "currentColor"
                }
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </span>
          ),
        }}
        className={classNames(
          className,
          styles.search,
          { [styles.error]: inError },
          inError ? "placeholder-red-900/50" : "placeholder-gray-500/50",
        )}
        classNamePrefix="select"
        filterOption={filterOption}
        formatCreateLabel={(input: string) => input}
        formatOptionLabel={formatOptionLabel}
        isClearable={true}
        isDisabled={false}
        isLoading={false}
        isSearchable={true}
        menuPortalTarget={document.querySelector("body")}
        name="color"
        noOptionsMessage={() => noOptionsMessage}
        onBlur={onBlur}
        onChange={(selectedValue: SingleValue<SearchOption>) =>
          onChange(selectedValue?.value)
        }
        onInputChange={(input: string) => {
          if (!hideUnlessSearch) return;
          if (input) {
            setMenuIsOpen(true);
          } else {
            setMenuIsOpen(false);
          }
        }}
        menuIsOpen={menuIsOpen}
        options={options}
        placeholder={fieldPlaceholder}
        styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }) }}
        value={selectedValue}
      />
    </>
  );
}

export default Search;
